import type { PackageVehicleReason } from '@/types/package';
import {
  conditionLabels,
  formatNumber,
  formatOdometer,
} from '@components/HandoverDetail/handoverDetailDisplay';

// Labels for the unit facts a reason quotes. Status and process type have no
// shared label map yet; these follow the vehicle list's wording.
const VEHICLE_STATUS_LABEL: Record<string, string> = {
  IDLE: 'Idle',
  ACTIVE: 'Active',
  LOST: 'Lost',
  IN_REPAIR: 'In Repair',
  BROKEN: 'Broken',
  RETURNED: 'Returned',
  RELEASED: 'Released',
};

const PROCESS_TYPE_LABEL: Record<string, string> = {
  AVAILABLE: 'Available',
  ISSUE: 'Issue',
  INCIDENT: 'Incident',
  REPOSSESSION: 'Repossession',
  MAINTENANCE: 'Maintenance',
  CANDIDATE_PERM_MAINTENANCE: 'Candidate Perm Maintenance',
};

const km = (value: unknown) => formatOdometer(Number(value));
const days = (value: unknown) => `${formatNumber(Number(value))} hari`;

const range = (
  expected: PackageVehicleReason['expected'],
  unit: (value: unknown) => string
): string => {
  if (!expected || typeof expected !== 'object' || Array.isArray(expected)) {
    return '';
  }
  const { min, max } = expected;
  if (min !== undefined && max !== undefined) {
    return `${unit(min)}–${unit(max)}`;
  }
  if (min !== undefined) return `minimal ${unit(min)}`;
  if (max !== undefined) return `maksimal ${unit(max)}`;
  return '';
};

const list = (
  expected: PackageVehicleReason['expected'],
  label: (value: string) => string = (value) => value
): string =>
  Array.isArray(expected)
    ? expected.map((v) => label(String(v))).join(', ')
    : '';

/** One reason as a sentence ops can act on. Unknown codes fall back to the code. */
export const describePackageVehicleReason = ({
  code,
  expected,
  actual,
}: PackageVehicleReason): string => {
  switch (code) {
    case 'STATUS_NOT_IDLE':
      return `Status unit ${VEHICLE_STATUS_LABEL[String(actual)] ?? actual}, bukan Idle`;
    case 'PROCESS_NOT_AVAILABLE':
      return `Sedang dalam proses ${PROCESS_TYPE_LABEL[String(actual)] ?? actual}`;
    case 'IS_BOOKING':
      return 'Sudah dibooking rental lain';
    case 'HELD_BY_OPEN_HANDOVER':
      return 'Masih tercatat di handover yang belum selesai';
    case 'ODOMETER_UNKNOWN':
      return `Odometer belum terbaca dari telemetry (aturan ${range(expected, km)})`;
    case 'ODOMETER_BELOW_MIN':
      return `Odometer ${km(actual)}, minimal ${km(expected)}`;
    case 'ODOMETER_ABOVE_MAX':
      return `Odometer ${km(actual)}, maksimal ${km(expected)}`;
    case 'AGE_UNKNOWN':
      return `Tanggal unit tiba belum diisi (aturan umur ${range(expected, days)})`;
    case 'AGE_BELOW_MIN':
      return `Umur unit ${days(actual)}, minimal ${days(expected)}`;
    case 'AGE_ABOVE_MAX':
      return `Umur unit ${days(actual)}, maksimal ${days(expected)}`;
    case 'CONDITION_NOT_ALLOWED':
      return `Kondisi ${conditionLabels[String(actual)] ?? actual}, aturan hanya ${list(
        expected,
        (value) => conditionLabels[value] ?? value
      )}`;
    case 'BUSINESS_UNIT_NOT_ALLOWED':
      return `Business unit ${actual ?? 'kosong'}, aturan hanya ${list(expected)}`;
    case 'CLIENT_NOT_ALLOWED':
      return actual === null || actual === undefined
        ? 'Unit tidak berasal dari PO client mana pun'
        : `Client unit (dari PO) #${actual}, aturan hanya ${list(
            expected,
            (value) => `#${value}`
          )}`;
    case 'NO_RULES':
      return 'Package belum punya aturan unit, jadi tidak ada unit yang cocok';
    default:
      return code;
  }
};
